import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

export default function AddPrincipalDialog({ 
  open, 
  handleClose, 
  setPrincipalData, 
  schoolList, 
  isLoading,
  isEdit,
  principalToEdit,
  setPrincipalToEdit
}) {
  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    reset,
  } = useForm({});

  const [selectedSchool, setSelectedSchool] = useState('');

  // Populate form when editing
  useEffect(() => {
    if (isEdit && principalToEdit && open) {
      setValue('fname', principalToEdit.fname || '');
      setValue('lname', principalToEdit.lname || '');
      setValue('email', principalToEdit.email || '');
      setValue('phone', principalToEdit.phone || '');
      setValue('gender', principalToEdit.gender || '');
      setSelectedSchool(principalToEdit.schoolId?.[0] || '');
    }
  }, [isEdit, principalToEdit, open, setValue]);

  const onSubmit = (data) => {
    // Validate form data
    if (!data.email || !data.fname || !data.lname || (!isEdit && !data.password)) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Prepare principal data (role is set to 'principal' for API compatibility)
    const principalPayload = {
      fname: data.fname,
      lname: data.lname,
      email: data.email,
      ...(data.password && { password: data.password }),
      phone: data.phone || '',
      gender: data.gender || '',
      schoolId: selectedSchool ? [selectedSchool] : [],
    };

    // Set the principal data to trigger the mutation in parent
    setPrincipalData(principalPayload);
    
    // Don't reset yet - let the parent component handle it after successful creation
  };

  const handleCloseDialog = () => {
    reset();
    setSelectedSchool('');
    if (setPrincipalToEdit) {
      setPrincipalToEdit({});
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        '& .MuiPaper-root': {
          width: { xs: '100% !important', sm: '649px' },
          minWidth: { xs: '90%', sm: 'auto' },
          height: 'max-content',
          borderRadius: '20px',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '20px' }}>
        <Typography variant="h4" sx={{ color: 'text.titles' }}>
          {isEdit ? 'Edit Principal' : 'Add Principal'}
        </Typography>
        <CloseIcon sx={{ cursor: 'pointer' }} onClick={handleCloseDialog} />
      </Box>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>

          <TextField
            name="fname"
            label="First Name"
            placeholder="Enter first name"
            fullWidth
            {...register('fname', {
              required: 'First name is required',
              maxLength: { value: 50, message: 'First name cannot exceed 50 characters' },
            })}
            error={errors && !!errors?.fname}
            helperText={errors?.fname?.message}
            type="text"
            sx={{ mb: '17px', '& .MuiFormHelperText-root': { margin: 0 } }}
          />

          <TextField
            name="lname"
            label="Last Name"
            placeholder="Enter last name"
            fullWidth
            {...register('lname', {
              required: 'Last name is required',
              maxLength: { value: 50, message: 'Last name cannot exceed 50 characters' },
            })}
            error={errors && !!errors?.lname}
            helperText={errors?.lname?.message}
            type="text"
            sx={{ mb: '17px', '& .MuiFormHelperText-root': { margin: 0 } }}
          />

          <TextField
            name="email"
            label="Email"
            placeholder="Enter email address"
            fullWidth
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            })}
            error={errors && !!errors?.email}
            helperText={errors?.email?.message}
            type="email"
            sx={{ mb: '17px', '& .MuiFormHelperText-root': { margin: 0 } }}
          />

          {!isEdit && (
            <TextField
              name="password"
              label="Password"
              placeholder="Enter password (minimum 6 characters)"
              fullWidth
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
              error={errors && !!errors?.password}
              helperText={errors?.password?.message}
              type="password"
              sx={{ mb: '17px', '& .MuiFormHelperText-root': { margin: 0 } }}
            />
          )}
          {isEdit && (
            <TextField
              name="password"
              label="New Password (Optional)"
              placeholder="Leave blank to keep current password"
              fullWidth
              {...register('password', {
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
              error={errors && !!errors?.password}
              helperText={errors?.password?.message}
              type="password"
              sx={{ mb: '17px', '& .MuiFormHelperText-root': { margin: 0 } }}
            />
          )}

          <TextField
            name="phone"
            label="Phone (Optional)"
            placeholder="Enter phone number"
            fullWidth
            {...register('phone', {
              maxLength: { value: 20, message: 'Phone number cannot exceed 20 characters' },
            })}
            error={errors && !!errors?.phone}
            helperText={errors?.phone?.message}
            type="text"
            sx={{ mb: '17px', '& .MuiFormHelperText-root': { margin: 0 } }}
          />

          <TextField
            select
            name="gender"
            label="Gender (Optional)"
            fullWidth
            value={watch('gender') || ''}
            {...register('gender')}
            onChange={(e) => setValue('gender', e.target.value)}
            sx={{ mb: '17px' }}
          >
            <MenuItem value="">Select Gender</MenuItem>
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>

          {/* School Selection */}
          <TextField
            select
            name="school"
            label="Partnership Site (Optional)"
            fullWidth
            value={selectedSchool}
            onChange={(e) => setSelectedSchool(e.target.value)}
            sx={{ mb: '17px' }}
          >
            <MenuItem value="">Select Partnership Site</MenuItem>
            {schoolList?.map((school) => (
              <MenuItem key={school._id} value={school._id}>
                {school.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>

        <Box sx={{ textAlign: 'right', pr: '31px', pb: '37px' }}>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            color="main"
            sx={{ width: '164px', height: '48px', mr: { xs: '0', sm: '4px' }, mb: { xs: '10px', sm: '0' } }}
            type="submit"
          >
            Save
          </LoadingButton>
          <Button
            variant="outlined"
            color="secondary"
            sx={{ width: '164px', height: '48px' }}
            onClick={handleCloseDialog}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Dialog>
  );
}

